import React, { useMemo, useState, useEffect } from "react";
import {
  useMsal,
  IMsalContext
} from "@azure/msal-react";
import { AuthenticationService } from "./Services/AuthenticationService";
import { getAppInsightsConnectionString, getLocalStorageItem, hasRole, setAppInsights, setFeatures, setHomeLocation, setLocalStorageItem } from "./Services/Global";
import { ApiService } from "./Services/ApiService";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { Pages } from "./Components/Pages";

// Material UI imports
import {
  ThemeProvider,
  Box,
  Alert,
  IconButton,
  Snackbar,
  CssBaseline
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Import Error Boundaries
import logger from './Services/LoggingService';
import { appTheme } from './theme/appTheme';

export const App: React.FC<{}> = () => {

  // Consolidated styles object - OPTIMIZED with useMemo to prevent recreation
  const styles = useMemo(() => ({
    // Main container styles
    appContainer: {
      height: '100vh',
      backgroundColor: appTheme.palette.background.default,
      display: 'flex',
      flexDirection: 'column'
    },
    
    // Unauthorized user styles
    unauthorizedContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      padding: appTheme.spacing(4),
      textAlign: 'center'
    },
    unauthorizedText: {
      color: appTheme.palette.text.primary,
      marginBottom: appTheme.spacing(2)
    },
    
    // Error notification styles
    errorSnackbar: {
      position: 'fixed',
      top: 80,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '90vw',
      maxWidth: '800px',
      zIndex: 1000
    },
    errorAlert: {
      width: '100%',
      backgroundColor: appTheme.palette.error.main,
      color: appTheme.palette.common.white,
      '& .MuiAlert-icon': {
        color: appTheme.palette.common.white
      },
      '& .MuiAlert-action': {
        color: appTheme.palette.common.white
      }
    },
    
    // Layout styles
    mainLayout: {
      display: 'flex',
      flexDirection: 'row',
      height: 'calc(100vh - 60px)', // Subtract navbar height
      overflow: 'hidden'
    },
    contentArea: {
      flex: 1,
      overflow: 'auto',
      height: '100%',
      boxShadow: appTheme.shadows[8],
      backgroundColor: appTheme.palette.background.paper
    }
  }), []);

  // Original state management - keeping functional for now
  const [signedIn, setSignedIn] = useState(false);
  const [isBusy, setIsBusy] = useState(true);
  const [isSme, setIsSme] = useState(false);
  const [isModeler, setIsModeler] = useState(false);
  const [isBlindTester, setIsBlindTester] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);
  const [blockUnassignedUsers, setBlockUnassignedUsers] = useState(false);
  const [errorData, setErrorData] = React.useState<string>('');
  const [sellerFeature, setSellerFeature] = useState(false);
  const [modelerFeature, setModelerFeature] = useState(false);
  const [showConditionalFields, setShowConditionalFields] = useState(false);

//   initializeIcons();

  const ctx: IMsalContext = useMsal();
  const authService: AuthenticationService = new AuthenticationService(ctx);
  const apiService = useMemo(() => new ApiService(ctx), [ctx]);

  useEffect(() => {
    let firstTimeUser = getLocalStorageItem('showTutorial');
    if (firstTimeUser === null) {
      setLocalStorageItem('showTutorial', 'true');
    }

    const appInsights = new ApplicationInsights({
      config: {
        connectionString: getAppInsightsConnectionString()
      }
    });

    appInsights.loadAppInsights();
    setAppInsights(appInsights);

    async function signInAndGetFeatures() {
      try {
        // Ensure authentication is properly initialized regardless of the route
        await authService.getAccessToken();
        setSignedIn(true);

        if (hasRole(...['SME'])) {
          setIsSme(true);
          setHomeLocation('/blindtests/dashboard')
        }
        else if (hasRole(...['Modeler'])) {
          setIsModeler(true);
          setHomeLocation('/modeling/dashboard')
        }
        else {
          setIsBlindTester(true);
          setHomeLocation('/about');
        }

        getFeaturesFromApi();
      } catch (error) {
        logger.error("Authentication failed during sign-in", error as Error, null, {
          component: 'App',
          method: 'signInAndGetFeatures'
        });
        if (error instanceof Error) {
          setErrorData(`Authentication error: ${error.message}`);
        } else {
          setErrorData("An unknown authentication error occurred");
        }
      }
    }

    /**
     * Get features from API and set state variables accordingly 
     */
    async function getFeaturesFromApi() {
      try {
        var response = await apiService.getAsync('settings/features');
        if (!response.ok) {
          logger.error("Failed to fetch features from API", new Error(response.statusText), {
            status: response.status,
            statusText: response.statusText
          }, {
            component: 'App',
            method: 'getFeaturesFromApi'
          });
        }

        const features = await response.json();
        setFeatures(features);

        setBlockUnassignedUsers(features["blockUnassignedUsers"]);
        setSellerFeature(features["sellerFeature"]);
        setModelerFeature(features["modelerFeature"]);
        setShowConditionalFields(features["showConditionalFields"]);

        setIsBusy(false);
      }
      catch {
        setIsBusy(false);
      }
    }

    signInAndGetFeatures();
  }, []);

  window.onunhandledrejection = function (e) {
    setErrorData("Oops, something went wrong: " + e.reason);
  }

  const dismissErrorHandler = () => {
    setErrorData('');
  }

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <Box sx={styles.appContainer}>
        {
          signedIn &&
            (unauthorized && blockUnassignedUsers) ?
            <Box sx={styles.unauthorizedContainer}>
              <Box sx={styles.unauthorizedText}>
                You are not authorized to use this application.
                <Box component="p" sx={{ margin: appTheme.spacing(1, 0) }}>
                  The application is intended for Sellers and SMEs.
                </Box>
                <Box component="p" sx={{ margin: appTheme.spacing(1, 0) }}>
                  (insert contact details here.)
                </Box>
              </Box>
            </Box>
            :
            <>
              {/* Error notification */}
              <Snackbar
                open={!!errorData}
                onClose={dismissErrorHandler}
                sx={styles.errorSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              >
                <Alert
                  severity="error"
                  sx={styles.errorAlert}
                  action={
                    <IconButton
                      size="small"
                      aria-label="close"
                      color="inherit"
                      onClick={dismissErrorHandler}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  {errorData}
                </Alert>
              </Snackbar>

              {
                isBusy === false ?
                  <Pages
                    isSme={isSme}
                    sellerFeatureActive={sellerFeature}
                    modelerFeatureActive={modelerFeature}
                    showConditionalFields={showConditionalFields}
                    isModeler={isModeler}
                    isBlindtester={isBlindTester}
                  />
                  :
                  <></>
              }
            </>
        }
      </Box>
    </ThemeProvider>
  );
}

export default App; 